import {
  BoldOutlined,
  ClearOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  RedoOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Card, Divider, Dropdown, Space, Tooltip } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import classNames from 'classnames';

import Heading from '@tiptap/extension-heading';
import Underline from '@tiptap/extension-underline';
import styles from './TipTapEditor.less';

interface AntdTiptapEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: number;
  /**
   * Stretch to fill a flex parent instead of sizing to `minHeight`: the card
   * becomes the flex item, the text area grows to the remaining space and
   * scrolls internally. Used by the composer so the writing area is the
   * drawer's leftover height rather than a fixed box with vacuum below it.
   */
  fill?: boolean;
}

const AntdTiptapEditor = forwardRef<any, AntdTiptapEditorProps>(
  (
    {
      value = '',
      onChange,
      placeholder = 'Type your text here…',
      readOnly = false,
      minHeight = 300,
      fill = false,
    },
    ref,
  ) => {
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
        }),
        Link.configure({
          openOnClick: true,
        }),
        Underline,
        Heading.configure({
          levels: [1, 2, 3],
        }),
        Placeholder.configure({
          placeholder,
        }),
      ],
      content: value || '',
      editable: !readOnly,
      onUpdate({ editor }) {
        const html = editor.getHTML();
        onChange?.(html);
      },
    });

    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
    }));

    // Update editor content if `value` changes from outside
    useEffect(() => {
      if (!editor) return;
      const current = editor.getHTML();
      if (value !== undefined && value !== current) {
        editor.commands.setContent(value, false);
      }
    }, [value, editor]);

    if (!editor) {
      return null;
    }

    const setLink = () => {
      const previousUrl = editor.getAttributes('link').href;
      const url = window.prompt('Link URL', previousUrl || '');

      if (url === null) return;

      if (url === '') {
        editor.chain().focus().unsetLink().run();
        return;
      }

      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    };

    const clearFormatting = () => {
      editor.chain().focus().clearNodes().unsetAllMarks().run();
    };
    // ---- Heading helpers ----
    const setHeadingLevel = (level: 1 | 2 | 3 | 'paragraph') => {
      const chain = editor.chain().focus();

      if (level === 'paragraph') {
        chain.setParagraph().run();
        return;
      }

      chain.toggleHeading({ level }).run();
    };

    const headingItems = [
      {
        key: 'paragraph',
        label: 'Paragraph',
        onClick: () => setHeadingLevel('paragraph'),
      },
      {
        key: 'h1',
        label: 'Heading 1',
        onClick: () => setHeadingLevel(1),
      },
      {
        key: 'h2',
        label: 'Heading 2',
        onClick: () => setHeadingLevel(2),
      },
      {
        key: 'h3',
        label: 'Heading 3',
        onClick: () => setHeadingLevel(3),
      },
    ];
    const currentHeadingLabel = () => {
      if (editor.isActive('heading', { level: 1 })) return 'H1';
      if (editor.isActive('heading', { level: 2 })) return 'H2';
      if (editor.isActive('heading', { level: 3 })) return 'H3';
      return 'Text';
    };

    return (
      <Card
        size="small"
        className={classNames(styles.editorCard, fill && styles.editorCardFill)}
        bodyStyle={{ padding: 0 }}
      >
        {!readOnly && (
          <>
            <div className={styles.toolbar}>
              <Space wrap size={0}>
                <Dropdown menu={{ items: headingItems }} trigger={['click']}>
                  <Button type="text" icon={<FontSizeOutlined />}>
                    {currentHeadingLabel()}
                  </Button>
                </Dropdown>

                <Divider type="vertical" />
                <Tooltip title="Bold">
                  <Button
                    type={editor.isActive('bold') ? 'primary' : 'text'}
                    icon={<BoldOutlined />}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                  />
                </Tooltip>
                <Tooltip title="Italic">
                  <Button
                    type={editor.isActive('italic') ? 'primary' : 'text'}
                    icon={<ItalicOutlined />}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                  />
                </Tooltip>
                <Tooltip title="Underline">
                  <Button
                    type={editor.isActive('underline') ? 'primary' : 'text'}
                    icon={<UnderlineOutlined />}
                    onClick={() =>
                      editor.chain().focus().toggleUnderline?.().run?.()
                    }
                    disabled={!editor.can().chain().focus().toggleBold().run()}
                  />
                </Tooltip>

                <Divider type="vertical" />

                <Tooltip title="Bullet list">
                  <Button
                    type={editor.isActive('bulletList') ? 'primary' : 'text'}
                    icon={<UnorderedListOutlined />}
                    onClick={() =>
                      editor.chain().focus().toggleBulletList().run()
                    }
                  />
                </Tooltip>
                <Tooltip title="Ordered list">
                  <Button
                    type={editor.isActive('orderedList') ? 'primary' : 'text'}
                    icon={<OrderedListOutlined />}
                    onClick={() =>
                      editor.chain().focus().toggleOrderedList().run()
                    }
                  />
                </Tooltip>

                <Divider type="vertical" />

                <Tooltip title="Add / edit link">
                  <Button
                    type={editor.isActive('link') ? 'primary' : 'text'}
                    icon={<LinkOutlined />}
                    onClick={setLink}
                  />
                </Tooltip>

                <Divider type="vertical" />

                <Tooltip title="Undo">
                  <Button
                    type="text"
                    icon={<UndoOutlined />}
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                  />
                </Tooltip>
                <Tooltip title="Redo">
                  <Button
                    type="text"
                    icon={<RedoOutlined />}
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                  />
                </Tooltip>

                <Divider type="vertical" />

                <Tooltip title="Clear formatting">
                  <Button
                    type="text"
                    icon={<ClearOutlined />}
                    onClick={clearFormatting}
                  />
                </Tooltip>
              </Space>
            </div>
            <Divider style={{ margin: 0 }} />
          </>
        )}

        {/*
        `minHeight` has to reach the contenteditable itself, not this wrapper:
        the box the user sees is the wrapper, but only `.ProseMirror` takes a
        caret. Sizing the wrapper left everything below the first line inert —
        clicking the middle of a 280px box focused nothing and the composer
        read as broken. Handed down as a custom property so the rule stays in
        the LESS module.
      */}
        <div
          className={styles.editorWrapper}
          style={
            { '--editor-min-height': `${minHeight}px` } as React.CSSProperties
          }
          // Clicking the padding/empty area around the text must land the
          // caret, not silently focus nothing (keystrokes then fall through to
          // global hotkeys). mousedown + preventDefault so the editor never
          // momentarily blurs on the way.
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              event.preventDefault();
              editor.chain().focus('end').run();
            }
          }}
        >
          <EditorContent editor={editor} />
        </div>
      </Card>
    );
  },
);

export default AntdTiptapEditor;
